import { useState } from 'react';
import type { Column, RowRenderProps, SortingRule } from 'react-table';
import { useDialogContext } from '../../../../contexts/DialogContext';
import SimpleReactTable, { MINIMUM_PAGE_SIZE } from '../../../generic/SimpleReactTable';
import TableHeader from '../../../generic/TableHeader';
import type { UsersStore } from '../../../../redux/users';
import type { UserInvitation } from '../../../../../typings/api';
import { useDeleteUserInvitation } from '../../../../hooks/api';
import SettingsTableRowActions from '../../shared/SettingsTableRowActions';
import { icons } from '../../../generic';

const getCreatedByUser = (userId: number | undefined, users: UsersStore['entities']): string => {
  if (userId) {
    return users[userId]?.full_name || users[userId]?.username || users[userId]?.email || 'Unknown';
  }
  return 'Unknown';
};

type InvitationRow = Pick<UserInvitation, 'email'> & {
  createdByUser: string;
};

type ColumnRenderParameter = { original: InvitationRow } & Omit<RowRenderProps, 'original'>;

type UserInvitationsTableProps = {
  invitations: UserInvitation[];
  users: UsersStore['entities'];
  searchQuery: string;
};

const UserInvitationsTable = (props: UserInvitationsTableProps) => {
  const [sortMode, setSortMode] = useState<SortingRule>({ id: 'email', desc: false });

  const rows: InvitationRow[] = props.invitations.map(invitation => ({
    email: invitation.email,
    createdByUser: getCreatedByUser(invitation.createdByUserId, props.users),
  }));

  let filteredRows: InvitationRow[] = rows;
  if (props.searchQuery) {
    const searchLower = props.searchQuery.toLowerCase();

    filteredRows = rows.filter(
      ({ email, createdByUser }) =>
        email.toLowerCase().includes(searchLower) || createdByUser.toLowerCase().includes(searchLower),
    );
  }

  const columns: Column[] = [
    {
      id: 'email',
      Header: (
        <TableHeader title="User" sort={{ id: 'email', currentMode: sortMode }} />
      ),
      accessor: 'email',
      width: 340,
      resizable: false,
      style: {
        textOverflow: 'ellipsis',
        display: 'block',
      },
      // eslint-disable-next-line react/no-unstable-nested-components -- This is fine for now.
      Cell: ({ original }: ColumnRenderParameter) => (
        <span>{original.email}</span>
      ),
    },
    {
      id: 'createdByUser',
      Header: (
        <TableHeader title="Invited By" sort={{ id: 'createdByUser', currentMode: sortMode }} />
      ),
      accessor: 'createdByUser',
      resizable: false,
      // eslint-disable-next-line react/no-unstable-nested-components -- This is fine for now.
      Cell: ({ original }: ColumnRenderParameter) => (
        <span>{original.createdByUser}</span>
      ),
      style: {
        whiteSpace: 'pre-line',
        overflow: 'visible',
        paddingRight: '15px',
      },
    },
    {
      id: 'actions',
      Header: <TableHeader title="Actions" />,
      accessor: 'actions',
      resizable: false,
      sortable: false,
      width: 70,
      // eslint-disable-next-line react/no-unstable-nested-components -- This is fine for now.
      Cell: ({ original }: ColumnRenderParameter) => (
        <RemoveUserInviteCell email={original.email} />
      ),
    },
  ];

  return (
    <SimpleReactTable
      data={filteredRows}
      columns={columns}
      defaultSortMode={[sortMode]}
      onSortedChanged={(newSortMode: SortingRule) => setSortMode(newSortMode)}
      showPagination={filteredRows.length > MINIMUM_PAGE_SIZE}
      cacheId="organizationUserInvitations"
    />
  );
};

/**
 * Cell renderer for the "Created By User" column.
 */
const RemoveUserInviteCell = ({ email }: { email: string }) => {
  const { mutate: deleteUserInvite } = useDeleteUserInvitation();
  const { onConfirm } = useDialogContext();

  //  Confirm before removing the invite.
  const onRemoveInvite = () =>
    onConfirm({
      title: 'Confirm remove invite?',
      body: (
        <p>
          Are you sure you want to remove invite for &quot;{email}&quot;?
        </p>
      ),
      onConfirm: () => deleteUserInvite(email),
    });

  return (
    <SettingsTableRowActions>
      <SettingsTableRowActions.IconButton
        icon={icons.trashRegular}
        onClick={onRemoveInvite}
        tooltipId={`delete-user-${email}`}
        tooltip="Remove invitation"
      />
    </SettingsTableRowActions>
  );
};

export default UserInvitationsTable;
